import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faApple, faAndroid, faWindows } from '@fortawesome/free-brands-svg-icons';
import { faDesktop } from '@fortawesome/free-solid-svg-icons';
import type { TesterDevice } from '@runhuman/shared';
import { DEVICE_META, sortedDevices } from '@runhuman/shared';

/**
 * Renders a tester's registered devices as compact FontAwesome SVG icons,
 * sized to sit inline with the alias on the Activity presence chip (#3968).
 *
 * The Activity iframe runs under a strict outbound URL allowlist, so we
 * tree-shake SVGs out of `@fortawesome/free-{brands,solid}-svg-icons` —
 * shipping ~5KB of icon paths instead of pulling the full FontAwesome CDN
 * bundle that the web dashboard loads via `<link>` (web's `<i className=...>`
 * is fine because FA classes are already on the page; the iframe doesn't
 * have that). Both surfaces still resolve through the shared `DEVICE_META`,
 * so labels + sort order stay in lockstep.
 *
 * The runtime guard at module load asserts that each entry in DEVICE_SVG_ICONS
 * matches the FontAwesome icon name encoded in `DEVICE_META[device].iconClass`
 * (`fa-{style} fa-{name}`). Adding a new TesterDevice now requires the SVG
 * import here AND the meta entry in `tester-meta.ts` — drift fails the build
 * fast in dev rather than silently rendering the wrong icon.
 */
const DEVICE_SVG_ICONS: Record<TesterDevice, IconDefinition> = {
  ios: faApple,
  android: faAndroid,
  pc: faWindows,
  mac: faDesktop,
};

for (const device of Object.keys(DEVICE_SVG_ICONS) as TesterDevice[]) {
  const expected = extractFaIconName(DEVICE_META[device].iconClass);
  const actual = DEVICE_SVG_ICONS[device].iconName;
  if (actual !== expected) {
    throw new Error(
      `TesterDeviceIcons: SVG icon for "${device}" is "${actual}" but DEVICE_META.iconClass resolves to "${expected}". Keep these in sync.`,
    );
  }
}

/** Extracts `apple` from `fa-brands fa-apple` so the guard above can compare against the SVG's `iconName`. */
function extractFaIconName(iconClass: string): string {
  const match = iconClass.match(/\bfa-(?:brands|solid|regular)\s+fa-([\w-]+)/);
  if (!match) {
    throw new Error(`TesterDeviceIcons: unrecognized FontAwesome class "${iconClass}" — expected "fa-{style} fa-{name}".`);
  }
  return match[1];
}

interface Props {
  readonly devices: readonly TesterDevice[];
}

export function TesterDeviceIcons({ devices }: Props) {
  if (devices.length === 0) return null;
  return (
    <span className="presence-row__devices">
      {sortedDevices(devices).map((device) => {
        const meta = DEVICE_META[device];
        return (
          <FontAwesomeIcon
            key={device}
            icon={DEVICE_SVG_ICONS[device]}
            className="presence-row__device-icon"
            title={meta.label}
            aria-label={meta.label}
          />
        );
      })}
    </span>
  );
}
